import React, { useState, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import {
  AI_ENGINEERING_CATEGORIES,
  AI_ENABLED_WORK_CATEGORIES,
  AI_LEVELS,
} from "config";
import { usePatchCvMutation } from "../../../lib/cvsSlice";
import { useGetAiSkillsQuery } from "../../../lib/skillsSlice";

function buildCategoryMap(items = []) {
  return items.reduce((acc, item) => {
    acc[item.category] = item;
    return acc;
  }, {});
}

function initForms(categories, existingMap) {
  return categories.reduce((acc, cat) => {
    const existing = existingMap[cat.key] || {};
    acc[cat.key] = {
      _id: existing._id || null,
      level: existing.level || "",
      skills: existing.skills || [],
      context: existing.context || "",
      isDirty: false,
    };
    return acc;
  }, {});
}

// ── Sub-components ────────────────────────────────────────────────────────────

const LevelBadge = ({ level }) => (
  <Chip label={level} size="small" variant="aiLevel" sx={{ mr: 1 }} />
);

const SkillChips = ({ skills }) => (
  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
    {skills.slice(0, 2).map((s) => (
      <Chip key={s} label={s} size="small" variant="aiSkill" />
    ))}
    {skills.length > 2 && (
      <Chip label={`+${skills.length - 2} More`} size="small" variant="aiSkill" />
    )}
  </Box>
);

const CategoryAccordion = ({ cat, form, skills, onUpdate, onSave, showContext, isLoading }) => {
  const hasData = !!form.level;

  return (
    <Accordion variant="aiCategory" disableGutters>
      <AccordionSummary variant="aiCategory" expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="aiCategoryLabel">{cat.label}</Typography>
            <Typography variant="aiCategoryDescription">{cat.description}</Typography>
            {hasData && form.skills.length > 0 && <SkillChips skills={form.skills} />}
          </Box>
          {hasData && <LevelBadge level={form.level} />}
        </Box>
      </AccordionSummary>

      <AccordionDetails variant="aiCategory">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            label="Level *"
            value={form.level}
            onChange={(e) => onUpdate(cat.key, "level", e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          >
            {AI_LEVELS.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </TextField>

          {skills.length > 0 && (
            <Autocomplete
              multiple
              options={skills}
              value={form.skills}
              onChange={(_, newValue) => onUpdate(cat.key, "skills", newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    size="small"
                    variant="aiSkill"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Skills & Experience"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  placeholder={form.skills.length === 0 ? "Select skills..." : ""}
                />
              )}
            />
          )}

          {showContext && (
            <TextField
              label="Additional Context (Optional)"
              value={form.context}
              onChange={(e) => onUpdate(cat.key, "context", e.target.value)}
              multiline
              rows={3}
              fullWidth
              size="small"
              inputProps={{ maxLength: 300 }}
              InputLabelProps={{ shrink: true }}
              placeholder="Add any additional context or details..."
              helperText={`${form.context.length}/300 characters`}
            />
          )}

          {form.isDirty && form.level && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => onSave(cat.key)}
                disabled={isLoading}
                sx={{ minWidth: "80px" }}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const AiMachineIntelligence = ({ aiEngineering = [], aiEnabledWork = [], cvId }) => {
  const engineeringMap = buildCategoryMap(aiEngineering);
  const enabledWorkMap = buildCategoryMap(aiEnabledWork);

  const [engineeringForms, setEngineeringForms] = useState(() =>
    initForms(AI_ENGINEERING_CATEGORIES, engineeringMap)
  );
  const [enabledWorkForms, setEnabledWorkForms] = useState(() =>
    initForms(AI_ENABLED_WORK_CATEGORIES, enabledWorkMap)
  );

  const [patchCv, { isLoading }] = usePatchCvMutation();

  const { data: aiSkillsFromDb = [] } = useGetAiSkillsQuery();

  const skillsBySubCategory = useMemo(() =>
    aiSkillsFromDb.reduce((acc, skill) => {
      if (!acc[skill.subCategory]) acc[skill.subCategory] = [];
      acc[skill.subCategory].push(skill.name);
      return acc;
    }, {}),
  [aiSkillsFromDb]);

  // ── Form updaters ───────────────────────────────────────────────────────────

  const updateEngineering = (key, field, value) => {
    setEngineeringForms((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value, isDirty: true },
    }));
  };

  const updateEnabledWork = (key, field, value) => {
    setEnabledWorkForms((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value, isDirty: true },
    }));
  };

  // ── Save handlers ───────────────────────────────────────────────────────────

  const saveEngineering = async (categoryKey) => {
    const form = engineeringForms[categoryKey];
    if (!form.isDirty || !form.level) return;

    const payload = {
      category: categoryKey,
      level: form.level,
      skills: form.skills,
      context: form.context,
    };
    if (form._id) payload._id = form._id;

    try {
      const updated = await patchCv({ cvId, aiEngineering: [payload] }).unwrap();
      const savedItem = updated.aiEngineering?.find((i) => i.category === categoryKey);
      if (savedItem) {
        setEngineeringForms((prev) => ({
          ...prev,
          [categoryKey]: { ...prev[categoryKey], _id: savedItem._id, isDirty: false },
        }));
      }
    } catch {
      toast.error("Failed to save");
    }
  };

  const saveEnabledWork = async (categoryKey) => {
    const form = enabledWorkForms[categoryKey];
    if (!form.isDirty || !form.level) return;

    const payload = {
      category: categoryKey,
      level: form.level,
      skills: form.skills,
    };
    if (form._id) payload._id = form._id;

    try {
      const updated = await patchCv({ cvId, aiEnabledWork: [payload] }).unwrap();
      const savedItem = updated.aiEnabledWork?.find((i) => i.category === categoryKey);
      if (savedItem) {
        setEnabledWorkForms((prev) => ({
          ...prev,
          [categoryKey]: { ...prev[categoryKey], _id: savedItem._id, isDirty: false },
        }));
      }
    } catch {
      toast.error("Failed to save");
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="aiSectionHeading" display="block" sx={{ mb: 0.25 }}>
          1. AI Engineering & Integration
        </Typography>
        <Typography variant="aiSectionSubtitle" display="block" sx={{ mb: 2 }}>
          Experience with AI systems, models, and infrastructure
        </Typography>
        {AI_ENGINEERING_CATEGORIES.map((cat) => (
          <CategoryAccordion
            key={cat.key}
            cat={cat}
            form={engineeringForms[cat.key]}
            skills={skillsBySubCategory[cat.key] || []}
            onUpdate={updateEngineering}
            onSave={saveEngineering}
            showContext
            isLoading={isLoading}
          />
        ))}
      </Box>

      <Box>
        <Typography variant="aiSectionHeading" display="block" sx={{ mb: 0.25 }}>
          2. AI-Enabled Work
        </Typography>
        <Typography variant="aiSectionSubtitle" display="block" sx={{ mb: 2 }}>
          Using AI tools to enhance productivity and work quality
        </Typography>
        {AI_ENABLED_WORK_CATEGORIES.map((cat) => (
          <CategoryAccordion
            key={cat.key}
            cat={cat}
            form={enabledWorkForms[cat.key]}
            skills={skillsBySubCategory[cat.key] || []}
            onUpdate={updateEnabledWork}
            onSave={saveEnabledWork}
            isLoading={isLoading}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AiMachineIntelligence;
